import { defineStore } from "pinia";
import type { ArtType, Artwork } from "~~/types/artwork";

interface UsersMeta {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

interface ArtistGalleryState {
    artworksFetching: boolean;
    filtering: boolean;
    artworks: Artwork[];
    meta: UsersMeta | null;

    // Artist specific
    artistId: string | null;

    // Query Params
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: "asc" | "desc";
    search: string;
    artType: ArtType;
    color: string | null;
}

export const useArtistGalleryStore = defineStore("ArtistGalleryStore", {
    state: (): ArtistGalleryState => ({
        artworksFetching: false,
        filtering: false,
        artworks: [],
        meta: null,

        artistId: null,

        page: 1,
        limit: 24, // Optimized for 3 or 4 column grids
        sortBy: "createdAt",
        sortOrder: "desc",

        search: "",
        artType: "",
        color: null,
    }),

    actions: {
        /**
         * Set the artist ID and fetch their works
         */
        async initArtistGallery(id: string) {
            this.artistId = id;
            this.page = 1;
            await this.getArtistArtworks(true);
        },

        async getArtistArtworks(primary = true): Promise<void> {
            if (!this.artistId) return;
            const { $api } = useNuxtApp()
            try {
                if (primary) {
                    this.artworksFetching = true;
                } else {
                    this.filtering = true;
                }

                const response = await $api<any>(`/art/gallery/${this.artistId}`, {
                    params: {
                        page: this.page,
                        limit: this.limit,
                        sortBy: this.sortBy,
                        sortOrder: this.sortOrder,
                        search: this.search,
                        artType: this.artType,
                        color: this.color,
                    },
                });

                this.artworks = response.data;
                this.meta = response.meta;
                if (!primary) {
                    this.scrollTop();
                }
            } catch (_e) {
            } finally {
                this.artworksFetching = false;
                this.filtering = false;
            }
        },

        scrollTop() {
            const element = document.getElementById("artist-grid-top");
            if (!element) return;
            setTimeout(() => {
                element.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                });
            }, 100);
        },

        async searchArtwork() {
            this.page = 1;
            await this.getArtistArtworks(false);
        },

        async filterByArtType(artTypeKey: ArtType) {
            this.page = 1;
            this.artType = this.artType === artTypeKey ? "" : artTypeKey;
            await this.getArtistArtworks(false);
        },

        async filterByColor(color: string) {
            this.page = 1;
            this.color = this.color === color ? null : color;
            await this.getArtistArtworks(false);
        },

        async clearSearch() {
            this.search = "";
            this.page = 1;
            await this.getArtistArtworks(false);
        },

        async changePage(page: number) {
            if (this.page === page) return;
            this.page = page;
            await this.getArtistArtworks(false);
        },

        async nextPage() {
            if (this.meta && this.page < this.meta.totalPages) {
                this.page++;
                await this.getArtistArtworks(false);
            }
        },

        async prevPage() {
            if (this.page > 1) {
                this.page--;
                await this.getArtistArtworks(false);
            }
        },
    },
});
